import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { CardPayment } from '../payments.types';
import { VchasnoKasaService } from '../../../services/vchasno-kasa.service';
import { BoardPaymentService } from '../../../services/board-payment.service';
import { TranslationManagerService } from '../../../core/i18n';
import { canRetryFiscal } from '../../prro/prro.types';
import { FiscalNotificationService } from '../../../services/fiscal-notification.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';

@Component({
  selector: 'app-payment-fiscal-status',
  standalone: false,
  templateUrl: './payment-fiscal-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PaymentFiscalStatusComponent implements OnInit {
  @Input({ required: true }) payment!: CardPayment;
  @Output() changed = new EventEmitter<void>();
  private readonly sidebar = inject(StackedSidebarService);
  private returnSidebarOpen = false;
  private readonly api = inject(VchasnoKasaService);
  private readonly feedback = inject(BoardPaymentService);
  private readonly translations = inject(TranslationManagerService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly fiscalNotifications = inject(FiscalNotificationService);
  protected readonly canRetry = canRetryFiscal;
  protected busy = false;
  protected get refunded(): number {
    return (this.payment.x_data?.fiscal_returns ?? []).reduce(
      (sum, entry) => sum + (entry.applied ? entry.amount : 0),
      0,
    );
  }
  openReturn(): void {
    if (this.returnSidebarOpen || this.payment.status !== 'paid' || !this.payment.fiscal_operation) return;
    this.returnSidebarOpen = true;
    this.sidebar
      .openSidebar<boolean>({ type: SidebarType.VchasnoKasaReturn, width: '600px' }, { payment: this.payment })
      .pipe(
        finalize(() => (this.returnSidebarOpen = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.changed.emit());
  }
  ngOnInit(): void {
    this.translations.loadModuleTranslations('prro').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
  protected retry(delivery = false): void {
    const operation = this.payment.fiscal_operation;
    if (this.busy || !operation) return;
    this.busy = true;
    const request: Observable<unknown> = delivery
      ? this.api.retryDelivery(this.payment.id)
      : this.api.retryReceipt(operation).pipe(switchMap((result) => this.api.waitForReceipt(result.id)));
    request
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.changed.emit();
        },
        error: (error) => this.feedback.error(error),
      });
  }
}
